import React from 'react';
import Dialog from './Dialog';

export interface ConfirmDialogProps {
    open: boolean;
    /** Заголовок решения: «Удалить вещь?», а не «Внимание». */
    title: React.ReactNode;
    /** Что именно произойдёт и обратимо ли это. */
    description?: React.ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    /** Необратимое действие: подтверждение красное (styleguide §Buttons). */
    destructive?: boolean;
    /** Идёт операция: кнопки заблокированы, окно не закрывается по Escape и фону. */
    busy?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    'data-testid'?: string;
}

/**
 * Единое подтверждение решения (U13).
 *
 * До него в приложении жили три паттерна: нативный `confirm()` (не стилизуется,
 * не читается как часть интерфейса, не знает тему), самодельное «Удалить? Да/Нет»
 * внутри страницы и полноценная модалка. Теперь один компонент поверх `Dialog`,
 * значит один и тот же клавиатурный контракт у всех подтверждений.
 */
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
    open,
    title,
    description,
    confirmLabel = 'Подтвердить',
    cancelLabel = 'Отмена',
    destructive = false,
    busy = false,
    onConfirm,
    onCancel,
    'data-testid': testId = 'confirm-dialog',
}) => (
    <Dialog
        open={open}
        onClose={onCancel}
        title={title}
        busy={busy}
        data-testid={testId}
        footer={
            <>
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={busy}
                    className="btn-secondary min-h-[44px]"
                    data-testid="confirm-dialog-cancel"
                >
                    {cancelLabel}
                </button>
                <button
                    type="button"
                    onClick={onConfirm}
                    disabled={busy}
                    className={`${destructive ? 'btn-danger' : 'btn-primary'} min-h-[44px]`}
                    data-testid="confirm-dialog-confirm"
                >
                    {confirmLabel}
                </button>
            </>
        }
    >
        {description && (
            <p className="text-sm text-gray-700 dark:text-gray-300">{description}</p>
        )}
    </Dialog>
);

export default ConfirmDialog;
